import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchApi } from '../../../helpers/fetch';
import {
  setTopNavbar,
  clearTopNavbar,
} from '../../Reducers/actions/navbarActions';
import { fetchSubscribe, fetchUserSubscriptions } from '../../Reducers/actions/creationsActions';
import ViewCreationSkeleton from '../ViewCreation/ViewCreationSkeleton';
import Icon from '../../components/Icon';
import ProfilePicture from '../../components/ProfilePicture/ProfilePicture';
import Unsubscribe from '../../components/Unsubscribe/Unsubscribe';
import { GENRE_ICONS } from '../../constants/genres';
import { fromCentsToPounds } from '../../../helpers/money';
import { capitalizeFirstLetter } from '../../../helpers/utilities';
import { getUserDisplayName } from '../../../helpers/userDisplayName';
import '../ViewCreation/ViewCreation.scss';
import './CreationAnalyticsDetail.scss';

const STAT_CARDS = [
  { key: 'likes', label: 'Likes', icon: 'like_creation' },
  { key: 'views', label: 'Views', icon: 'eye_views' },
  { key: 'comments', label: 'Comments', icon: 'comment_analytics' },
  { key: 'favourites', label: 'Favourites', icon: 'star' },
  { key: 'shares', label: 'Shares', icon: 'share_android' },
  { key: 'inLibrary', label: 'In Library', icon: 'library' },
];

const DUMMY_SUBSCRIBERS = [
  { user_id: 'd1', firstName: 'Marie', lastName: 'Dupont', username: '@maried', profilePicture: null },
  { user_id: 'd2', firstName: 'Jean', lastName: 'Martin', username: '@jeanm', profilePicture: null },
  { user_id: 'd3', firstName: 'Sophie', lastName: 'Bernard', username: '@sophieb', profilePicture: null },
];

const DUMMY_BUYERS = [
  { user_id: 'db1', username: '@buyer1', status: 'Completed' },
  { user_id: 'db2', username: '@buyer2', status: 'Completed' },
  { user_id: 'db3', username: '@buyer3', status: 'Completed' },
];

const DUMMY_CONTRIBUTORS = [
  { user_id: 'dc1', username: '@contrib1', amount: 2500 },
  { user_id: 'dc2', username: '@contrib2', amount: 1500 },
];

function CreationAnalyticsDetail() {
  const { creationId } = useParams();
  const dispatch = useDispatch();
  const auth_user_id = useSelector((state) => state.auth.user?.user_id);
  const auth_user = useSelector((state) => state.auth.user);
  const view_user = useSelector((state) => state.creation.view_user);

  const [creation, setCreation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [unsubscribeTarget, setUnsubscribeTarget] = useState(null);

  const fetchData = useCallback(async () => {
    if (!creationId) return;
    setLoading(true);
    setError(null);
    try {
      const [creationRes, analyticsRes] = await Promise.all([
        fetchApi(`/api/creations/detail/${creationId}`),
        fetchApi(`/api/creations/${creationId}/analytics`).catch(() => null),
      ]);

      const raw = creationRes?.success ? creationRes?.data : creationRes;
      const creationData = Array.isArray(raw) ? raw[0] : raw;
      setCreation(creationData || null);

      if (analyticsRes?.success && analyticsRes?.data) {
        setAnalytics(analyticsRes.data);
      } else {
        setAnalytics(null);
      }
    } catch (err) {
      setError(err?.message ?? 'Failed to load creation analytics');
      setCreation(null);
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [creationId]);

  useEffect(() => {
    dispatch(
      setTopNavbar({
        title: 'creation analytics',
        showBackArrow: true,
      })
    );
    fetchData();
    return () => dispatch(clearTopNavbar());
  }, [dispatch, fetchData]);

  useEffect(() => {
    if (!auth_user_id) return;
    dispatch(fetchUserSubscriptions(auth_user_id));
  }, [auth_user_id, dispatch]);

  const rawMedia = creation?.media;
  const media = Array.isArray(rawMedia) && rawMedia.length > 0
    ? rawMedia[0]
    : (rawMedia && !Array.isArray(rawMedia) ? rawMedia : null);

  const getCategoryIcon = useMemo(() => {
    const cat = typeof creation?.category === 'string' ? creation.category : creation?.category?.name;
    return GENRE_ICONS[(cat || '')?.toLowerCase()] || 'creations';
  }, [creation?.category]);

  const categoryLabel = useMemo(() => {
    if (media?.media_format) return capitalizeFirstLetter(media.media_format);
    if (media?.media_url) return 'Digital';
    const cat = typeof creation?.category === 'string' ? creation.category : creation?.category?.name;
    return cat ? capitalizeFirstLetter(cat) : 'Creation';
  }, [creation?.category, media]);

  const editionText = useMemo(() => {
    if (creation?.edition_text) return creation.edition_text;
    if (media?.max_stock) {
      const type = media.max_stock > 1 ? 'Limited' : 'Unique';
      const stock = media.current_stock ?? 0;
      const max = media.max_stock ?? 0;
      return `${type} edition${max > 0 ? `, ${stock}/${max} available` : ''}`;
    }
    const sz = creation?.creation_sizes?.[0];
    if (sz?.quantity_available != null) {
      return `Limited edition, ${sz.quantity_available} copies left`;
    }
    return 'Unlimited edition';
  }, [creation, media]);

  const primaryTag = useMemo(() => {
    const tag = creation?.tag;
    if (tag) return typeof tag === 'string' ? tag : (tag?.name ?? '');
    const primary = (creation?.tags || []).find((t) => t?.is_primary);
    return primary ? (typeof primary === 'string' ? primary : primary?.name ?? '') : '';
  }, [creation?.tag, creation?.tags]);

  const secondaryTags = useMemo(() => {
    const tags = creation?.tags || [];
    return tags.filter((t) => !t?.is_primary).map((t) => typeof t === 'string' ? t : (t?.name ?? ''));
  }, [creation?.tags]);

  const getStatValue = (key) => {
    if (analytics?.[key] != null) return analytics[key];
    if (!creation) return 0;
    switch (key) {
      case 'likes':
        return creation.total_likes ?? (Array.isArray(creation.likes) ? creation.likes.length : 0);
      case 'views':
        return creation.total_views ?? 0;
      case 'comments':
        return creation.total_comments ?? (Array.isArray(creation.comments) ? creation.comments.length : 0);
      case 'favourites':
        return creation.saves_count ?? (Array.isArray(creation.is_saved) ? creation.is_saved.length : 0);
      case 'shares':
        return analytics?.shares ?? 0;
      case 'inLibrary':
        return analytics?.inLibrary ?? creation.library_count ?? 0;
      default:
        return 0;
    }
  };

  const useDummyData = !analytics;
  const subscribers = analytics?.newSubscribers ?? (useDummyData ? DUMMY_SUBSCRIBERS : []);
  const buyers = analytics?.buyers ?? (useDummyData ? DUMMY_BUYERS : []);

  const creatorSubscriptions = auth_user?.subscriptions ?? view_user?.subscriptions ?? [];
  const getIsSubscribed = (sub) =>
    (creatorSubscriptions || []).some((s) => (s.user_id ?? s.id) === (sub.user_id ?? sub.id));

  const handleSubscribe = (subscribed_to_id, subscribe, sub) => {
    dispatch(fetchSubscribe(subscribed_to_id, subscribe));
    dispatch({
      type: 'SUBSCRIBE_SUCCESS',
      payload: { subscribe, subscriber_id: auth_user_id, subscribed_to_id, user: { ...sub, user_id: subscribed_to_id, is_subscribed: subscribe } },
    });
  };

  const handleUnsubscribeConfirm = () => {
    if (unsubscribeTarget) {
      handleSubscribe(unsubscribeTarget.user_id ?? unsubscribeTarget.id, false, unsubscribeTarget);
      setUnsubscribeTarget(null);
    }
  };

  const handleSubscribeClick = (sub) => {
    const isSubscribed = getIsSubscribed(sub);
    const subId = sub.user_id ?? sub.id;
    if (isSubscribed) {
      setUnsubscribeTarget(sub);
    } else {
      handleSubscribe(subId, true, sub);
    }
  };
  const contributors = analytics?.contributors ?? (useDummyData ? DUMMY_CONTRIBUTORS : []);
  const monthlyContributors = analytics?.monthlyContributors ?? (useDummyData ? DUMMY_CONTRIBUTORS.slice(0, 1) : []);

  const salesKpis = analytics?.salesKpis ?? (useDummyData ? {
    totalSales: 13,
    totalEarnings: '€ 2,898.74',
    unitsSold: 15,
    avgOrderValue: '€ 193.25',
  } : { totalSales: 0, totalEarnings: '€ 0', unitsSold: 0, avgOrderValue: '€ 0' });

  const contributionsKpis = analytics?.contributionsKpis ?? (useDummyData ? {
    monthlyCount: 2,
    monthlyAmount: '€ 40.00',
    oneShotCount: 2,
    oneShotAmount: '€ 40.00',
  } : { monthlyCount: 0, monthlyAmount: '€ 0', oneShotCount: 0, oneShotAmount: '€ 0' });

  const isNotFree = Number(creation?.price) > 0;
  const displayPrice = parseFloat(creation?.price) || 0;

  if (loading) return <ViewCreationSkeleton />;
  if (error) {
    return (
      <div className="creation-analytics-detail creation-analytics-detail__error">
        <p>{error}</p>
      </div>
    );
  }
  if (!creation) {
    return (
      <div className="creation-analytics-detail creation-analytics-detail__error">
        <p>Creation not found</p>
      </div>
    );
  }

  return (
    <div className="creation-analytics-detail" role="main" aria-label="Creation analytics">
      <div className="container-creation creation-analytics-detail__view-section">
        <div className={`thumbnail-creation ${media?.media_type === 'pdf' ? 'pdf-thumbnail' : ''}`}>
          {(creation.thumbnail_url || creation.media_url || media?.signed_url) ? (
            <img
              src={creation.thumbnail_url || creation.media_url || media?.signed_url}
              alt={creation.title || 'Creation preview'}
              className="img-thumbnail"
            />
          ) : (
            <div className="img-thumbnail creation-analytics-detail__hero-placeholder" />
          )}
        </div>

        <div className="mx-16 mb-16">
          <p className="fs-md fg-dark my-16 fw-medium-bold">{creation.title || 'Untitled'}</p>

          <div className="d-flex aic fs-real fw-medium">
            <Icon alias={getCategoryIcon} width={32} color="#0A0F1E" />
            <span className="ml-8">{categoryLabel} </span>
            <span className="dot" />
            <span className="mb-0">{editionText}</span>
          </div>
        </div>

        <div className="tags-container d-flex aic mx-16 mb-24">
          {primaryTag && <p className="category-tag primary">{capitalizeFirstLetter(primaryTag)}</p>}
          {secondaryTags.map((tag, i) => (
            <span className="category-tag" key={i}>
              {`#${capitalizeFirstLetter(tag).trim()}`}
            </span>
          ))}
        </div>

        {isNotFree && (
          <div className="price-container d-flex aic mx-16 p-16">
            <span className="fg-pink fw-medium d-flex aic fs-regular fw-bold">
              € {displayPrice.toFixed(2)}
              <Icon alias="info_v2" color="#FF8CDC" width={16} className="ml-4" />
            </span>
          </div>
        )}
      </div>

      <div className="creation-analytics-detail__content">
        <div className="creation-analytics-detail__stats">
          {STAT_CARDS.map(({ key, label, icon }) => (
            <div key={key} className="creation-analytics-detail__stat-card">
              <Icon alias={icon} width={32} height={32} color="#FF8CDC" />
              <span className="creation-analytics-detail__stat-value">
                {
                getStatValue(key).toLocaleString()
               }
              </span>
              <span className="creation-analytics-detail__stat-label">{label}</span>
            </div>
          ))}
        </div>

        {subscribers.length > 0 && (
          <section className="creation-analytics-detail__section">
            <div className="creation-analytics-detail__section-header">
              <h2>New subscribers</h2>
              <span>{subscribers.length} subscribers</span>
            </div>
            <ul className="creation-analytics-detail__list">
              {subscribers.slice(0, 3).map((sub) => {
                const isSubscribed = getIsSubscribed(sub);
                const subId = sub.user_id ?? sub.id;
                return (
                  <li key={subId} className="creation-analytics-detail__account-row">
                    <div className="creation-analytics-detail__avatar">
                      <ProfilePicture
                        imgUrl={sub.profile_picture || sub.avatar || sub.profilePicture}
                        no_edit
                        width={40}
                        height={40}
                      />
                    </div>
                    <div className="creation-analytics-detail__account-info">
                      <span className="creation-analytics-detail__account-name">
                        {getUserDisplayName(sub)}
                      </span>
                      <span className="creation-analytics-detail__account-handle">
                        {sub.username || sub.handle}
                      </span>
                    </div>
                    <button
                      type="button"
                      className={`creation-analytics-detail__subscribe-btn ${isSubscribed ? 'creation-analytics-detail__subscribe-btn--subscribed' : ''}`}
                      onClick={() => handleSubscribeClick(sub)}
                      aria-label={isSubscribed ? 'Unsubscribe' : 'Subscribe'}
                    >
                      {isSubscribed ? 'Subscribed' : 'Subscribe'}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {unsubscribeTarget && (
          <Unsubscribe
            user={{
              user_id: unsubscribeTarget.user_id ?? unsubscribeTarget.id,
              userName: unsubscribeTarget.username ?? unsubscribeTarget.handle ?? getUserDisplayName(unsubscribeTarget),
              profilePicture: unsubscribeTarget.profile_picture ?? unsubscribeTarget.profilePicture ?? unsubscribeTarget.avatar,
              ...unsubscribeTarget,
            }}
            onClose={() => setUnsubscribeTarget(null)}
            onSubmit={handleUnsubscribeConfirm}
          />
        )}

        <section className="creation-analytics-detail__section">
          <div className="creation-analytics-detail__section-header">
            <h2>Sales and Earnings</h2>
            <span>{buyers.length} buyers</span>
          </div>
          <div className="creation-analytics-detail__kpi-grid">
            <div className="creation-analytics-detail__kpi-card">
              <span className="creation-analytics-detail__kpi-label">Total Sales</span>
              <span className="creation-analytics-detail__kpi-value">
                {typeof salesKpis.totalSales === 'number' ? salesKpis.totalSales : salesKpis.totalSales ?? 0}
              </span>
            </div>
            <div className="creation-analytics-detail__kpi-card">
              <span className="creation-analytics-detail__kpi-label">Total Earnings</span>
              <span className="creation-analytics-detail__kpi-value">
                {salesKpis.totalEarnings ?? fromCentsToPounds(0)}
              </span>
            </div>
            <div className="creation-analytics-detail__kpi-card">
              <span className="creation-analytics-detail__kpi-label">Units Sold</span>
              <span className="creation-analytics-detail__kpi-value">
                {salesKpis.unitsSold ?? 0}
              </span>
            </div>
            <div className="creation-analytics-detail__kpi-card">
              <span className="creation-analytics-detail__kpi-label">Avg Order Value</span>
              <span className="creation-analytics-detail__kpi-value">
                {salesKpis.avgOrderValue ?? fromCentsToPounds(0)}
              </span>
            </div>
          </div>
          <div className="creation-analytics-detail__table">
            <div className="creation-analytics-detail__table-header">
              <span>Buyers</span>
              <span>Status</span>
            </div>
            {buyers.length > 0 ? (
              buyers.map((b) => (
                <div key={b.user_id || b.id} className="creation-analytics-detail__table-row-creation-analytics">
                  <span className="fg-red">{b.username || b.name || 'Buyer'}</span>
                  <span className="creation-analytics-detail__status">
                    <span className="creation-analytics-detail__status-dot" />
                    {b.status || 'Completed'}
                  </span>
                </div>
              ))
            ) : (
              <p className="creation-analytics-detail__empty">No buyers yet</p>
            )}
          </div>
        </section>

        <section className="creation-analytics-detail__section">
          <div className="creation-analytics-detail__section-header">
            <h2>Contributions</h2>
            <span>
              {(monthlyContributors.length + contributors.length)} contributors
            </span>
          </div>
          <div className="creation-analytics-detail__kpi-grid">
            <div className="creation-analytics-detail__kpi-card">
              <span className="creation-analytics-detail__kpi-label">Monthly contributors</span>
              <span className="creation-analytics-detail__kpi-value">
                {contributionsKpis.monthlyCount ?? monthlyContributors.length}
              </span>
            </div>
            <div className="creation-analytics-detail__kpi-card">
              <span className="creation-analytics-detail__kpi-label">Monthly amount</span>
              <span className="creation-analytics-detail__kpi-value">
                {contributionsKpis.monthlyAmount ?? fromCentsToPounds(0)}
              </span>
            </div>
          </div>
          {monthlyContributors.length > 0 && (
            <div className="creation-analytics-detail__table">
              <div className="creation-analytics-detail__table-header">
                <span>Monthly contributors</span>
                <span>Amount</span>
              </div>
              {monthlyContributors.map((c) => (
                <div key={c.user_id || c.id} className="creation-analytics-detail__table-row-creation-analytics">
                  <span className="fg-red">{c.username || c.name || 'Contributor'}</span>
                  <span>{typeof c.amount === 'number' ? fromCentsToPounds(c.amount) : (c.amount ?? fromCentsToPounds(0))}</span>
                </div>
              ))}
            </div>
          )}
          <div className="creation-analytics-detail__kpi-grid">
            <div className="creation-analytics-detail__kpi-card">
              <span className="creation-analytics-detail__kpi-label">Contributors</span>
              <span className="creation-analytics-detail__kpi-value">
                {contributionsKpis.oneShotCount ?? contributors.length}
              </span>
            </div>
            <div className="creation-analytics-detail__kpi-card">
              <span className="creation-analytics-detail__kpi-label">Total amount</span>
              <span className="creation-analytics-detail__kpi-value">
                {contributionsKpis.oneShotAmount ?? fromCentsToPounds(0)}
              </span>
            </div>
          </div>
          <div className="creation-analytics-detail__table">
            <div className="creation-analytics-detail__table-header">
              <span>Contributors</span>
              <span>Amount</span>
            </div>
            {contributors.length > 0 ? (
              contributors.map((c) => (
                <div key={c.user_id || c.id} className="creation-analytics-detail__table-row-creation-analytics">
                  <span className="fg-red">{c.username || c.name || 'Contributor'}</span>
                  <span>{typeof c.amount === 'number' ? fromCentsToPounds(c.amount) : (c.amount ?? fromCentsToPounds(0))}</span>
                </div>
              ))
            ) : (
              <p className="creation-analytics-detail__empty">No contributors yet</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default CreationAnalyticsDetail;
